import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { api, formatINR } from "@/lib/api";
import { toast } from "sonner";
import { Calendar, Sparkles, Users, ArrowRight, Plus, Trash2, CheckCircle2, AlertTriangle } from "lucide-react";

const OCCASIONS = [
  { k: "wedding", label: "Wedding" },
  { k: "reception", label: "Reception" },
  { k: "business", label: "Business" },
  { k: "casual", label: "Casual" },
  { k: "festival", label: "Festival" },
  { k: "graduation", label: "Graduation" },
];
const GARMENTS = ["suit", "tuxedo", "blazer", "shirt", "trousers", "sherwani", "ethnic", "accessory"];

export default function EventPlanner() {
  const { user, loading } = useAuth();
  const [events, setEvents] = useState([]);
  const [recommend, setRecommend] = useState({ products: [], garments: [], lead_times: {} });
  const [form, setForm] = useState({
    occasion: "wedding",
    title: "Our Wedding",
    event_date: new Date(Date.now() + 90 * 86400000).toISOString().slice(0, 10),
    location: "",
    budget: 250000,
    notes: "",
    members: [{ name: "", role: "Self", garments: [] }],
  });

  const refresh = async () => {
    if (!user) return;
    const r = await api.get("/events"); setEvents(r.data);
  };
  useEffect(() => { refresh(); }, [user]);
  useEffect(() => {
    api.get(`/events/recommend?occasion=${form.occasion}`).then(({ data }) => setRecommend(data));
  }, [form.occasion]);

  const addMember = () => setForm((f) => ({ ...f, members: [...f.members, { name: "", role: "Guest", garments: [] }] }));
  const removeMember = (idx) => setForm((f) => ({ ...f, members: f.members.filter((_, i) => i !== idx) }));
  const updateMember = (idx, key, val) => setForm((f) => ({ ...f, members: f.members.map((m, i) => i === idx ? { ...m, [key]: val } : m) }));
  const toggleGarment = (idx, g) => updateMember(idx, "garments", form.members[idx].garments.includes(g) ? form.members[idx].garments.filter((x) => x !== g) : [...form.members[idx].garments, g]);

  const save = async () => {
    if (!user) return toast("Sign in first");
    if (!form.members.length || !form.members[0].name) return toast.error("Add at least one party member");
    try {
      const { data } = await api.post("/events", { ...form, budget: Number(form.budget) });
      toast.success(`Event ${data.id} planned`);
      refresh();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  if (loading) return <div className="sq-container py-20 sq-eyebrow">Loading planner…</div>;
  if (!user) return <div className="sq-container py-20"><Link to="/auth" className="sq-btn-primary">Sign in to plan</Link></div>;

  return (
    <main className="sq-container py-12" data-testid="event-planner-page">
      <div className="sq-eyebrow text-squire-jeans">Occasion · Backward Timeline</div>
      <h1 className="font-display text-5xl md:text-6xl mt-2">Event Planner</h1>
      <p className="text-[#4B5563] mt-3 max-w-xl">Tell us the occasion and event date. We'll plan the outfits for you and your party — and back-compute the safe order date.</p>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-10 mt-10">
        <section className="lg:col-span-2 border border-black/10 p-6">
          <div className="sq-eyebrow">Occasion</div>
          <div className="flex flex-wrap gap-2 mt-2">
            {OCCASIONS.map((o) => (
              <button key={o.k} data-testid={`ev-occ-${o.k}`} onClick={() => setForm({ ...form, occasion: o.k })} className={`px-4 py-2 text-xs uppercase tracking-[0.2em] border ${form.occasion === o.k ? "border-squire-jeans bg-squire-jeans text-white" : "border-black/20"}`}>{o.label}</button>
            ))}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-6">
            <label className="block"><span className="sq-eyebrow">Title</span>
              <input data-testid="ev-title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="w-full bg-transparent border-b border-black/30 focus:border-squire-jeans outline-none py-2" />
            </label>
            <label className="block"><span className="sq-eyebrow flex items-center gap-1"><Calendar size={12} /> Event Date</span>
              <input data-testid="ev-date" type="date" value={form.event_date} onChange={(e) => setForm({ ...form, event_date: e.target.value })} className="w-full bg-transparent border-b border-black/30 focus:border-squire-jeans outline-none py-2" />
            </label>
            <label className="block"><span className="sq-eyebrow">Location</span>
              <input data-testid="ev-location" value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} className="w-full bg-transparent border-b border-black/30 focus:border-squire-jeans outline-none py-2" />
            </label>
            <label className="block"><span className="sq-eyebrow">Budget (₹)</span>
              <input data-testid="ev-budget" type="number" value={form.budget} onChange={(e) => setForm({ ...form, budget: e.target.value })} className="w-full bg-transparent border-b border-black/30 focus:border-squire-jeans outline-none py-2" />
            </label>
          </div>

          <div className="mt-8">
            <div className="flex items-center justify-between">
              <div className="sq-eyebrow flex items-center gap-1"><Users size={12} /> Party · {form.members.length}</div>
              <button data-testid="ev-add-member" onClick={addMember} className="text-xs flex items-center gap-1 border border-black/20 px-3 py-1 hover:border-squire-jeans"><Plus size={12} /> Add member</button>
            </div>
            <div className="space-y-3 mt-3">
              {form.members.map((m, i) => (
                <div key={i} className="border border-black/10 p-4" data-testid={`ev-member-${i}`}>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                    <label className="block md:col-span-2"><span className="sq-eyebrow text-[10px]">Name</span>
                      <input data-testid={`ev-member-name-${i}`} value={m.name} onChange={(e) => updateMember(i, "name", e.target.value)} className="w-full bg-transparent border-b border-black/30 outline-none py-1 text-sm" />
                    </label>
                    <label className="block"><span className="sq-eyebrow text-[10px]">Role</span>
                      <input data-testid={`ev-member-role-${i}`} value={m.role} onChange={(e) => updateMember(i, "role", e.target.value)} className="w-full bg-transparent border-b border-black/30 outline-none py-1 text-sm" />
                    </label>
                  </div>
                  <div className="mt-3">
                    <div className="sq-eyebrow text-[10px]">Garments</div>
                    <div className="flex flex-wrap gap-2 mt-2">
                      {GARMENTS.map((g) => (
                        <button key={g} data-testid={`ev-member-g-${i}-${g}`} onClick={() => toggleGarment(i, g)} className={`text-xs px-3 py-1 border ${m.garments.includes(g) ? "border-squire-jeans bg-squire-jeans text-white" : "border-black/30"}`}>
                          {g} <span className="ml-1 opacity-60">{recommend.lead_times?.[g] || 14}d</span>
                        </button>
                      ))}
                    </div>
                  </div>
                  {form.members.length > 1 && (
                    <button data-testid={`ev-member-remove-${i}`} onClick={() => removeMember(i)} className="text-xs text-[#4B5563] hover:text-squire-jeans mt-3 flex items-center gap-1"><Trash2 size={12} /> Remove</button>
                  )}
                </div>
              ))}
            </div>
          </div>

          <button data-testid="ev-save" onClick={save} className="sq-btn-primary mt-6">Plan & Save Timeline</button>
        </section>

        <section>
          <div className="border border-black/10 p-5">
            <div className="sq-eyebrow flex items-center gap-1"><Sparkles size={12} /> Curated for {form.occasion}</div>
            <div className="mt-3 space-y-1 text-xs text-[#4B5563]">Suggested garments: {recommend.garments?.join(" · ")}</div>
            <div className="grid grid-cols-2 gap-3 mt-4">
              {recommend.products?.slice(0, 6).map((p) => (
                <Link key={p.id} to={`/product/${p.id}`} className="block" data-testid={`ev-suggest-${p.id}`}>
                  <img src={p.images?.[0]} alt={p.name} className="w-full aspect-[3/4] object-cover" />
                  <div className="font-display text-xs mt-1 leading-tight">{p.name}</div>
                  <div className="sq-eyebrow text-[10px]">{formatINR(p.price)}</div>
                </Link>
              ))}
              {!recommend.products?.length && <div className="col-span-2 text-xs text-[#4B5563]">No curated products yet — admin can add via Lookbook.</div>}
            </div>
          </div>
        </section>
      </div>

      {/* Saved events */}
      <section className="mt-16">
        <div className="sq-eyebrow">Your events · {events.length}</div>
        <div className="space-y-3 mt-3">
          {events.length === 0 && <div className="text-sm text-[#4B5563]">No events saved yet.</div>}
          {events.map((e) => (
            <div key={e.id} className="border border-black/10 p-5" data-testid={`ev-row-${e.id}`}>
              <div className="flex flex-wrap justify-between gap-3">
                <div>
                  <div className="font-display text-2xl">{e.title}</div>
                  <div className="sq-eyebrow mt-1">{e.id} · {e.occasion} · {e.event_date?.slice(0, 10)} · {e.location || "—"} · {e.members?.length} member(s)</div>
                </div>
                <div className={`flex items-center gap-2 px-3 py-2 text-xs ${e.timeline?.on_time ? "bg-squire-jeans text-white" : "bg-red-50 text-red-600 border border-red-300"}`}>
                  {e.timeline?.on_time ? <CheckCircle2 size={14} /> : <AlertTriangle size={14} />}
                  {e.timeline?.on_time ? "On time" : "At risk"}
                </div>
              </div>
              {e.timeline?.warning && <div className="mt-3 text-xs text-red-600">{e.timeline.warning}</div>}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-4">
                <TimelineCell label="Start by" date={e.timeline?.start_by} />
                <TimelineCell label="First fitting" date={e.timeline?.fitting_1} />
                <TimelineCell label="Second fitting" date={e.timeline?.fitting_2} />
                <TimelineCell label="Ready by" date={e.timeline?.ready_by} highlight />
              </div>
              {e.members?.length > 0 && (
                <div className="grid grid-cols-2 md:grid-cols-3 gap-2 mt-4">
                  {e.members.map((m, i) => (
                    <div key={i} className="bg-[#F5F5F5] p-3 text-xs">
                      <div className="font-display">{m.name}</div>
                      <div className="text-[#4B5563]">{m.role} · {m.garments?.join(", ")}</div>
                    </div>
                  ))}
                </div>
              )}
              <div className="mt-4">
                <Link to="/bespoke" className="text-xs text-squire-jeans hover:underline flex items-center gap-1">Begin bespoke for this event <ArrowRight size={12} /></Link>
              </div>
            </div>
          ))}
        </div>
      </section>
    </main>
  );
}

function TimelineCell({ label, date, highlight }) {
  if (!date) return null;
  const d = new Date(date);
  return (
    <div className={`p-3 border ${highlight ? "border-squire-jeans bg-[#F5F5F5]" : "border-black/10"}`}>
      <div className="sq-eyebrow text-[10px]">{label}</div>
      <div className="font-display text-lg mt-1">{d.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" })}</div>
    </div>
  );
}
